/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField, LuisTextFieldProps } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';

const messages = defineMessages({
    appId: {
        id: 'applicationSettings.appInfoCard.appId',
        defaultMessage: 'App ID'
    },
    appName: {
        id: 'applicationSettings.appInfoCard.appName',
        defaultMessage: 'App name'
    },
    appCulture: {
        id: 'applicationSettings.appInfoCard.appCulture',
        defaultMessage: 'Culture'
    },
    appDescription: {
        id: 'applicationSettings.appInfoCard.appDescription',
        defaultMessage: 'App description'
    },
    optional: {
        id: 'applicationSettings.appInfoCard.optional',
        defaultMessage: '(optional)'
    },
    editAppName: {
        id: 'applicationSettings.appInfoCard.editAppName',
        defaultMessage: 'Edit app name'
    },
    renameAppToastStart: {
        id: 'applicationSettings.appInfoCard.renameAppToastStart',
        defaultMessage: 'Updating app ...'
    },
    renameAppToastSuccess: {
        id: 'applicationSettings.appInfoCard.renameAppToastSuccess',
        defaultMessage: 'Update app completed'
    },
    renameAppToastFailed: {
        id: 'applicationSettings.appInfoCard.renameAppToastFailed',
        defaultMessage: 'Update app failed'
    }
});

const { styleComponent } = getStylist('AppInfoCard');

const DescriptionArea = styleComponent<LuisTextFieldProps>(LuisTextField)('DescriptionArea', theme => ({
    maxWidth: theme.descriptionMaxWidth
}));

/**
 * A component that displays application information in the
 * application settings page in the manage pane.
 *
 * @param props The input for the component.
 */
export const AppInfoCard = observer(() => {
    const { rootStore, dispatcher } = useGlobalState();
    const { name, description, culture, id } = rootStore.currentLuisAppStore;

    const { localize } = useLocalization();
    const [isAppNameEditable, setIsAppNameEditable] = React.useState(false);

    const { formState, formStateDto, updateFormState } = useFormState<Pick<LuisAppStore, 'name' | 'description'>>({
        name: [name, FormValidators.createRequiredValidator()],
        description: [description]
    });

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);

    // Ensures that form state is in sync with props when they get updated from the parent.
    React.useEffect(() => updateFormState({ name, description }), [name, description]);

    /**
     * Renames the app with the given data.
     */
    const handleRenameApp = (dto: Pick<LuisAppStore, 'name' | 'description'>) => {
        dispatcher.dispatch('renameApp', {
            name: dto.name,
            description: dto.description,
            toastData: {
                running: { title: localize(messages.renameAppToastStart) },
                completed: { title: localize(messages.renameAppToastSuccess) },
                failed: { title: localize(messages.renameAppToastFailed) }
            }
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <Stack horizontal verticalFill verticalAlign="center">
                    <LuisText variant="mediumPlus">{localize(messages.appName)}</LuisText>
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        hidden={canChangeAppSettingsAndDetails}
                        content={canChangeAppSettingsAndDetails && localize(messages.editAppName)}
                        componentSurroundedByToolTip={
                            <ActionButton
                                hidden={isAppNameEditable}
                                iconProps={{ iconName: 'edit' }}
                                data-automation-id="appInfoEditName"
                                title={localize(messages.editAppName)}
                                ariaLabel={localize(messages.editAppName)}
                                onClick={() => setIsAppNameEditable(true)}
                                disabled={!canChangeAppSettingsAndDetails}
                            />
                        }
                    />
                </Stack>

                {!isAppNameEditable && <LuisText data-automation-id="appInfoName">{formState.fields.name.value}</LuisText>}

                {isAppNameEditable && (
                    <LuisTextField
                        data-automation-id="appInfoNameTextField"
                        autoFocus
                        value={formState.fields.name.value}
                        errorMessage={formState.fields.name.errorMessage}
                        onChange={(_, newName) => updateFormState({ name: newName })}
                        validateOnLoad={false}
                        onEnterKeyUp={() => {
                            if (formState.form.valid) {
                                handleRenameApp(formStateDto);
                                setIsAppNameEditable(false);
                            }
                        }}
                    />
                )}
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText>
                    <LuisText variant="mediumPlus">{localize(messages.appDescription)}</LuisText>
                    &nbsp;
                    {localize(messages.optional)}
                </LuisText>

                <DescriptionArea
                    ariaLabel={`${localize(messages.appDescription)} ${localize(messages.optional)}`}
                    data-automation-id="appInfoDescriptionTextField"
                    value={formState.fields.description.value}
                    multiline={description.length > 64}
                    onChange={(_, newDescription: string) => updateFormState({ description: newDescription })}
                    onEnterKeyUp={() => handleRenameApp(formStateDto)}
                    disabled={!canChangeAppSettingsAndDetails}
                />
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus">{localize(messages.appId)}</LuisText>

                <Stack horizontal verticalAlign="center" verticalFill>
                    <CopyButton textToCopy={id as string} label={localize(messages.appId)} />
                    <span data-automation-id="appInfoId">{id}</span>
                </Stack>
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus">{localize(messages.appCulture)}</LuisText>
                <LuisText data-automation-id="appInfoCulture" variant="mediumPlus">
                    {culture}
                </LuisText>
            </Stack>
        </Stack>
    );
});
